import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Ambulance, ArrowRight, ShieldCheck, Mail, RefreshCw } from "lucide-react";
import axios from "axios";
import { setPassengerSession } from "../../passengerAuth";
import "./Passenger.css";

export default function PassengerVerifyEmail() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const emailParam = searchParams.get("email");

  const [otp, setOtp] = useState("");
  const [loading, setLoading] = useState(false);
  const [resending, setResending] = useState(false);
  const [error, setError] = useState("");
  const [successMsg, setSuccessMsg] = useState("");

  useEffect(() => {
    if (!emailParam) {
      navigate("/passenger/signup");
    }
  }, [emailParam, navigate]);

  const handleVerify = async (e) => {
    e.preventDefault();
    setError("");
    setSuccessMsg("");

    if (!otp || otp.length < 6) {
      setError("Please enter the 6-digit verification code.");
      return;
    }

    try {
      setLoading(true);
      const res = await axios.post(`${process.env.REACT_APP_BASE_URL}/passengerVerifyEmailWeb`, {
        email: emailParam,
        code: otp,
      });

      const token = res?.data?.accessToken;
      const passengerData = res?.data?.passengerData;

      if (!token || !passengerData) {
        setError("Verification failed. Please try again.");
        setLoading(false);
        return;
      }

      setPassengerSession({ token, passengerData });
      navigate("/passenger");
    } catch (err) {
      setError(err?.response?.data?.msg || "Verification failed. Please check your code.");
      setLoading(false);
    }
  };

  const handleResend = async () => {
    setError("");
    setSuccessMsg("");
    setOtp("");
    try {
      setResending(true);
      await axios.post(`${process.env.REACT_APP_BASE_URL}/passengerResendOTPWeb`, {
        email: emailParam,
      });
      setSuccessMsg("Verification code resent! Please check your email inbox (and spam folder).");
    } catch (err) {
      setError(err?.response?.data?.msg || "Failed to resend the code.");
    } finally {
      setResending(false);
    }
  };

  return (
    <div className="pax-login-wrapper">
      {/* LEFT PANEL */}
      <div className="pax-login-left">
        <div className="pax-login-left-content">
          <div className="pax-brand-icon">
            <Ambulance size={40} color="#fff" />
          </div>
          <h1 className="pax-brand-title">ለAmbulance</h1>
          <p className="pax-brand-subtitle">Passenger Portal</p>

          <div className="pax-features" style={{ marginTop: '40px' }}>
            <div className="pax-feature-item">
              <div className="pax-feature-icon"><ShieldCheck size={18} /></div>
              <div>
                <div className="pax-feature-label">Secure Verification</div>
                <div className="pax-feature-desc">Protecting your health data</div>
              </div>
            </div>
            <div className="pax-feature-item">
              <div className="pax-feature-icon"><Mail size={18} /></div>
              <div>
                <div className="pax-feature-label">Check Your Inbox</div>
                <div className="pax-feature-desc">We've sent a 6-digit code</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* RIGHT PANEL */}
      <div className="pax-login-right">
        <div className="pax-login-form-wrapper">
          <div className="pax-form-header">
            <h2>Verify Email</h2>
            <p>
              We sent a 6-digit code to <strong>{emailParam}</strong>. 
              Please enter it below to activate your account.
            </p>
          </div>

          {error && (
            <div className="pax-error-alert" style={{ marginBottom: 20 }}>
              <span>⚠</span> {error}
            </div>
          )}

          {successMsg && (
            <div className="pax-error-alert" style={{ background: "#f0fdf4", borderColor: "#bbf7d0", color: "#166534", marginBottom: 20 }}>
              <span>✅</span> {successMsg}
            </div>
          )}

          <form onSubmit={handleVerify} className="pax-form">
            <div className="pax-form-group">
              <label>Verification Code</label>
              <div className="pax-input-wrapper">
                <span className="pax-input-icon"><ShieldCheck size={17} /></span>
                <input
                  type="text"
                  value={otp}
                  onChange={(e) => setOtp(e.target.value.replace(/[^0-9]/g, '').slice(0, 6))}
                  placeholder="e.g. 123456"
                  maxLength={6}
                  required
                  style={{ letterSpacing: '4px', fontSize: '1.2rem', fontWeight: 600 }}
                  autoComplete="one-time-code"
                />
              </div>
            </div>

            <button
              type="submit"
              className="pax-submit-btn"
              disabled={loading || otp.length < 6}
            >
              {loading ? (
                <span className="pax-btn-loading">
                  <span className="pax-spinner" /> Verifying…
                </span>
              ) : (
                <span className="pax-btn-content">
                  Verify Account <ArrowRight size={18} />
                </span>
              )}
            </button>
          </form>

          <div className="pax-form-footer">
            <div className="pax-divider"><span>Didn't receive the email?</span></div>
            <p style={{ textAlign: "center", marginTop: 16 }}>
              <button
                type="button"
                onClick={handleResend}
                disabled={resending}
                style={{
                  background: 'none',
                  border: 'none',
                  color: '#1d4ed8',
                  fontWeight: 600,
                  fontSize: '0.95rem',
                  cursor: resending ? 'not-allowed' : 'pointer',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '6px'
                }}
              >
                {resending ? <RefreshCw size={16} className="pax-spinner" /> : <RefreshCw size={16} />} 
                {resending ? "Resending..." : "Resend Verification Code"}
              </button>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
